import React from 'react';
import env from '../../../env';

const CALLBACK_NAME = 'onReCaptchaV2Loaded';
const SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?onload=${CALLBACK_NAME}&render=explicit`;

let loadPromise = null;

// Loads api.js once per page, on demand. Explicit rendering is required because
// client-side routing means the form is usually mounted well after page load.
function loadReCaptcha() {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.render) {
      resolve(window.grecaptcha);
      return;
    }

    window[CALLBACK_NAME] = () => resolve(window.grecaptcha);

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('reCAPTCHA script failed to load'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

// "I'm not a robot" tick box. onChange gets the token when ticked, and null when
// the token expires (~2 min) or the widget errors. Parents reset it via ref.
export const ReCaptchaV2 = React.forwardRef(function ReCaptchaV2(props, ref) {
  const { onChange, theme = 'light', size = 'normal' } = props;
  const containerRef = React.useRef(null);
  const widgetIdRef = React.useRef(null);
  const [loadError, setLoadError] = React.useState(null);

  // keep the latest callback without re-rendering the widget
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  React.useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current === null || !window.grecaptcha) return;
      try {
        window.grecaptcha.reset(widgetIdRef.current);
      } catch (e) {
        // widget is already gone, nothing to reset
      }
    }
  }));

  React.useEffect(() => {
    let cancelled = false;

    if (!env.reCaptchaSiteKey) {
      setLoadError('reCAPTCHA is not configured. Please contact us by phone or email.');
      return;
    }

    loadReCaptcha()
      .then((grecaptcha) => {
        if (cancelled || !containerRef.current) return;
        if (widgetIdRef.current !== null || containerRef.current.childNodes.length > 0) return;

        widgetIdRef.current = grecaptcha.render(containerRef.current, {
          sitekey: env.reCaptchaSiteKey,
          theme: theme,
          size: size,
          callback: (token) => onChangeRef.current && onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current && onChangeRef.current(null),
          'error-callback': () => onChangeRef.current && onChangeRef.current(null)
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError('Could not load reCAPTCHA. Please disable any ad blocker for this site and reload the page.');
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="recaptcha">
      <div ref={containerRef}></div>
      {loadError && <div className="error" role="alert">{loadError}</div>}
    </div>
  );
});
